import { ApiPayments } from './payments';
import { apiFetch } from './base';

export class ApiIntegrations extends ApiPayments {
  // ============ SPRINT 56 — SAML SSO + SCIM (firm-tier enterprise) ============

  /** B7 — SAML IdP config CRUD (firm-admin). */
  async listSamlConfigs(firmId: string): Promise<any[]> {
    return this.request(`/sso/saml/firm/${firmId}/configs`);
  }
  async createSamlConfig(payload: any): Promise<any> {
    return this.request('/sso/saml/configs', { method: 'POST', body: JSON.stringify(payload) });
  }
  async updateSamlConfig(id: string, payload: any): Promise<any> {
    return this.request(`/sso/saml/configs/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
  }
  async disableSamlConfig(id: string): Promise<void> {
    return this.request(`/sso/saml/configs/${id}/disable`, { method: 'POST' });
  }

  /** SP metadata URL the firm-admin shares with their IdP. */
  getSamlSpMetadataUrl(firmId: string): string {
    return `/api/sso/saml/firm/${firmId}/sp-metadata.xml`;
  }

  /** B8 — SCIM bearer token management (firm-admin). */
  async createScimToken(firmId: string, description?: string): Promise<any> {
    return this.request(`/scim/admin/firm/${firmId}/tokens`, {
      method: 'POST', body: JSON.stringify({ description: description || '' }),
    });
  }
  async listScimTokens(firmId: string): Promise<any[]> {
    return this.request(`/scim/admin/firm/${firmId}/tokens`);
  }
  async revokeScimToken(tokenId: string): Promise<void> {
    return this.request(`/scim/admin/tokens/${tokenId}/revoke`, { method: 'POST' });
  }

  /** B8 — Group → condo membership mappings. */
  async addScimGroupMapping(payload: {
    firmId: string; groupName: string; condoId: string; role?: string;
  }): Promise<any> {
    return this.request('/scim/admin/group-mappings', {
      method: 'POST', body: JSON.stringify(payload),
    });
  }
  async deleteScimGroupMapping(id: string): Promise<void> {
    return this.request(`/scim/admin/group-mappings/${id}`, { method: 'DELETE' });
  }
  async listScimGroupMappings(firmId: string): Promise<any[]> {
    return this.request(`/scim/admin/firm/${firmId}/group-mappings`);
  }

  // ============ SPRINT 55 — INTEGRATIONS PHASE 2 (access ctrl + calendar) ============

  /**
   * B5/B6 — Same OAuth pattern as Sprint 54 (beginIntegrationConnect),
   * but the provider param now accepts BUTTERFLYMX / BRIVO / LATCH /
   * GOOGLE_CALENDAR / OUTLOOK_CALENDAR. The Sprint 54
   * beginIntegrationConnect / disconnect / runSync / listForCondo
   * methods continue to work for the new providers — no separate
   * hooks needed.
   */

  /** B6 — Connect a resident calendar (Google or Outlook). */
  async connectCalendar(payload: {
    condoId: string;
    provider: 'GOOGLE_CALENDAR' | 'OUTLOOK_CALENDAR';
    accessToken: string;
    refreshToken?: string;
    expiresAt?: string;
    calendarId?: string;
  }): Promise<any> {
    return this.request('/calendar/connect', {
      method: 'POST', body: JSON.stringify(payload),
    });
  }

  async disconnectCalendar(subscriptionId: string): Promise<void> {
    return this.request(`/calendar/${subscriptionId}/disconnect`, { method: 'POST' });
  }

  async listMyCalendars(): Promise<any[]> {
    return this.request('/calendar/me');
  }

  /** Returns the ICS feed URL for a condo. Admin shares with board
   *  members; their calendar app subscribes. */
  getCondoIcsFeedUrl(condoId: string): string {
    return `/api/calendar/condo/${condoId}/bookings.ics`;
  }

  // ============ SPRINT 54 — INTEGRATIONS PHASE 1 (QBO + bank sync) ============

  /** B1/B4 — Begin OAuth flow; returns provider authorize URL the UI redirects to. */
  async beginIntegrationConnect(provider: 'QBO' | 'FLINKS' | 'PLAID', condoId: string, csrf?: string): Promise<{ authorizeUrl: string }> {
    const qs = new URLSearchParams({ condoId });
    if (csrf) qs.set('csrf', csrf);
    return this.request(`/integrations/${provider.toLowerCase()}/begin?${qs}`, { method: 'POST' });
  }

  async disconnectIntegration(connectionId: string): Promise<void> {
    return this.request(`/integrations/${connectionId}/disconnect`, { method: 'POST' });
  }

  /** Trigger a manual sync. Daily cron runs at 02:30 automatically. */
  async runIntegrationSync(connectionId: string): Promise<any> {
    return this.request(`/integrations/${connectionId}/sync`, { method: 'POST' });
  }

  async listIntegrationsForCondo(condoId: string): Promise<any[]> {
    return this.request(`/integrations/condo/${condoId}`);
  }

  async getIntegrationSyncHistory(connectionId: string): Promise<any[]> {
    return this.request(`/integrations/${connectionId}/history`);
  }

  // ============ SPRINT 53 — FRONT-DESK SCAFFOLD (visitor QR + lobby) ============

  /** F49 — Issue a QR pass for a pre-authorised visitor. */
  async issueVisitorPass(payload: {
    authorizationId: string;
    validFrom: string;   // ISO datetime
    validTo: string;
    issuedToEmail?: string;
  }): Promise<any> {
    return this.request('/visitor-passes/issue', {
      method: 'POST', body: JSON.stringify(payload),
    });
  }

  /** Front-desk scan endpoint. Token is the QR contents. */
  async redeemVisitorPass(token: string): Promise<any> {
    return this.request(`/visitor-passes/${encodeURIComponent(token)}/redeem`, { method: 'POST' });
  }

  async revokeVisitorPass(passId: string): Promise<void> {
    return this.request(`/visitor-passes/${passId}/revoke`, { method: 'POST' });
  }

  /** Lobby dashboard — expected visitors today. */
  async listExpectedVisitorsToday(condoId: string): Promise<any[]> {
    return this.request(`/visitor-passes/condo/${condoId}/today`);
  }

  async listVisitorPasses(condoId: string): Promise<any[]> {
    return this.request(`/visitor-passes/condo/${condoId}`);
  }

  async getVisitorPassAudit(passId: string): Promise<any[]> {
    return this.request(`/visitor-passes/${passId}/audit`);
  }

  // ============ SPRINT 52 — CSV IMPORTS + SELF-SERVE ONBOARDING ============

  /**
   * B27 / B28 — Upload a CSV for preview. Returns an ImportJob in
   * PREVIEW state with per-row outcomes. Nothing applies to domain
   * tables yet — the admin reviews the preview, fixes errors if
   * any, then calls commitCsvImport.
   *
   * type ∈ UNIT_CSV / OWNER_CSV / FEE_CSV / PAYMENT_CSV
   */
  async previewCsvImport(condoId: string, type: string, file: File): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    // apiFetch already prepends the /api base — pass the path WITHOUT a
    // leading '/api' (otherwise we'd hit '/api/api/import-jobs/...').
    const url = `/import-jobs/preview?condoId=${condoId}&type=${type}`;
    const res = await apiFetch(url, { method: 'POST', body: form });
    if (!res.ok) throw new Error(`Import preview failed: ${res.status}`);
    return res.json();
  }

  async commitCsvImport(jobId: string): Promise<any> {
    return this.request(`/import-jobs/commit/${jobId}`, { method: 'POST' });
  }

  async listImportJobs(condoId: string): Promise<any[]> {
    return this.request(`/import-jobs/jobs/condo/${condoId}`);
  }

  /** Download the per-row error report as a CSV blob. */
  async downloadImportErrorReport(jobId: string): Promise<Blob> {
    const res = await apiFetch(`/import-jobs/${jobId}/errors.csv`);
    if (!res.ok) throw new Error(`Error report download failed: ${res.status}`);
    return res.blob();
  }

  // ============ SPRINT 51 — COMMS COMPOSER + SMS + PUSH + TEMPLATES ============

  /** B15 — Cost preview before send. Returns recipient count + SMS cost. */
  async previewAnnouncement(payload: {
    condoId: string; title: string; body: string;
    channels: Array<'EMAIL' | 'SMS' | 'PUSH' | 'LOBBY_DISPLAY'>;
    targetAudience?: 'ALL' | 'OWNERS' | 'BOARD';
    category?: string; priority?: string;
  }): Promise<{
    totalRecipients: number;
    smsRecipients: number;
    estimatedSmsCostCents: number;
    estimatedSmsCostDollars: number;
  }> {
    return this.request('/announcement-composer/preview', {
      method: 'POST', body: JSON.stringify(payload),
    });
  }

  /** B15 — Send the announcement to the chosen channels. */
  async sendAnnouncement(payload: {
    condoId: string; title: string; body: string;
    channels: Array<'EMAIL' | 'SMS' | 'PUSH' | 'LOBBY_DISPLAY'>;
    targetAudience?: 'ALL' | 'OWNERS' | 'BOARD';
    category?: string; priority?: string;
  }): Promise<any> {
    return this.request('/announcement-composer/send', {
      method: 'POST', body: JSON.stringify(payload),
    });
  }

  /** B21 — Announcement template CRUD. */
  async listAnnouncementTemplates(condoId: string): Promise<any[]> {
    return this.request(`/announcement-composer/templates/condo/${condoId}`);
  }
  async createAnnouncementTemplate(payload: {
    condoId: string; name: string; subject: string; body: string;
    channels: string;       // JSON array string, e.g. '["EMAIL","SMS"]'
    category?: string;
  }): Promise<any> {
    return this.request('/announcement-composer/templates', {
      method: 'POST', body: JSON.stringify(payload),
    });
  }
  async updateAnnouncementTemplate(id: string, payload: any): Promise<any> {
    return this.request(`/announcement-composer/templates/${id}`, {
      method: 'PUT', body: JSON.stringify(payload),
    });
  }
  async deleteAnnouncementTemplate(id: string): Promise<void> {
    return this.request(`/announcement-composer/templates/${id}`, { method: 'DELETE' });
  }

  // ============ SPRINT 48 — FIRM-TIER WORKSPACE + PORTFOLIO ============

  /** F59 — Management firm CRUD (super-admin). */
  async listManagementFirms(): Promise<any[]> {
    return this.request('/management-firms');
  }
  async getManagementFirm(id: string): Promise<any> {
    return this.request(`/management-firms/${id}`);
  }
  async createManagementFirm(payload: any): Promise<any> {
    return this.request('/management-firms', { method: 'POST', body: JSON.stringify(payload) });
  }
  async updateManagementFirm(id: string, payload: any): Promise<any> {
    return this.request(`/management-firms/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
  }

  /** F61 — Portfolio roll-up KPIs per firm. */
  async getPortfolioForFirm(firmId: string): Promise<any> {
    return this.request(`/portfolio/firm/${firmId}`);
  }

  /** F63 — Cross-condo vendor scorecard roll-up. */
  async getFirmVendorRollup(firmId: string): Promise<any[]> {
    return this.request(`/portfolio/firm/${firmId}/vendors`);
  }

  /** F64 — Onboarding templates (firm-admin). */
  async listOnboardingTemplates(firmId: string): Promise<any[]> {
    return this.request(`/onboarding-templates/firm/${firmId}`);
  }
  async createOnboardingTemplate(payload: any): Promise<any> {
    return this.request('/onboarding-templates', { method: 'POST', body: JSON.stringify(payload) });
  }
  async updateOnboardingTemplate(id: string, payload: any): Promise<any> {
    return this.request(`/onboarding-templates/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
  }
  async deleteOnboardingTemplate(id: string): Promise<void> {
    return this.request(`/onboarding-templates/${id}`, { method: 'DELETE' });
  }
  async applyOnboardingTemplate(templateId: string, condoId: string): Promise<any> {
    return this.request(`/onboarding-templates/${templateId}/apply/${condoId}`, { method: 'POST' });
  }
  async getOnboardingHistoryForCondo(condoId: string): Promise<any[]> {
    return this.request(`/onboarding-templates/provisions/condo/${condoId}`);
  }

  // ============ SPRINT 47 — DOCUMENT FOLDERS + ACCESS LEVELS ============

  /** F55 — folder tree for a condo. Returns FolderView with document counts. */
  async listDocumentFolders(condoId: string): Promise<any[]> {
    return this.request(`/document-folders/condo/${condoId}`);
  }

  async listDocumentsInFolder(folderId: string, condoId: string): Promise<any[]> {
    return this.request(`/document-folders/${folderId}/documents?condoId=${condoId}`);
  }

  async createDocumentFolder(payload: {
    condoId: string; parentFolderId?: string; name: string;
    description?: string; displayOrder?: number;
  }): Promise<any> {
    return this.request('/document-folders', {
      method: 'POST', body: JSON.stringify(payload),
    });
  }

  async updateDocumentFolder(id: string, payload: {
    name: string; description?: string; displayOrder?: number; parentFolderId?: string;
  }): Promise<any> {
    return this.request(`/document-folders/${id}`, {
      method: 'PUT', body: JSON.stringify(payload),
    });
  }

  async deleteDocumentFolder(id: string): Promise<void> {
    return this.request(`/document-folders/${id}`, { method: 'DELETE' });
  }

  // ============ SPRINT 46 — AMENITY COMPLETENESS ============

  /** F52 — book with Stripe deposit + fee charge. */
  async bookAmenityWithPayment(payload: {
    amenityId: string; amenityName?: string;
    condoId: string; unitNumber: string; ownerName: string; ownerId?: string;
    date: string; startTime: string; endTime: string;
    attendees?: number; purpose?: string; notes?: string;
    stripeCustomerId?: string;
  }): Promise<any> {
    return this.request('/amenity-lifecycle/bookings/with-payment', {
      method: 'POST', body: JSON.stringify(payload),
    });
  }

  /** F53 — cancel with auto-refund per amenity policy. */
  async cancelAmenityBookingWithRefund(bookingId: string, reason?: string): Promise<any> {
    return this.request(`/amenity-lifecycle/bookings/${bookingId}/cancel`, {
      method: 'POST', body: JSON.stringify({ reason: reason || '' }),
    });
  }

  /** F54 — blackout windows (admin). */
  async addAmenityBlackout(payload: {
    amenityId: string; condoId: string;
    startDate: string; endDate: string; reason: string;
  }): Promise<any> {
    return this.request('/amenity-lifecycle/blackouts', {
      method: 'POST', body: JSON.stringify(payload),
    });
  }
  async removeAmenityBlackout(id: string): Promise<void> {
    return this.request(`/amenity-lifecycle/blackouts/${id}`, { method: 'DELETE' });
  }
  async listAmenityBlackouts(amenityId: string): Promise<any[]> {
    return this.request(`/amenity-lifecycle/blackouts/amenity/${amenityId}`);
  }

  /** F54 — waitlist (resident-initiated). */
  async joinAmenityWaitlist(payload: {
    amenityId: string; condoId: string; ownerId: string;
    ownerName?: string; unitNumber?: string;
    requestedDate: string; requestedStart?: string; requestedEnd?: string;
  }): Promise<any> {
    return this.request('/amenity-lifecycle/waitlist', {
      method: 'POST', body: JSON.stringify(payload),
    });
  }

  async confirmWaitlistOffer(waitlistId: string, stripeCustomerId?: string): Promise<any> {
    return this.request(`/amenity-lifecycle/waitlist/${waitlistId}/confirm`, {
      method: 'POST', body: JSON.stringify({ stripeCustomerId }),
    });
  }

  // ============ SPRINT 45 — ONLINE E-VOTING + QUORUM ============

  /** F37 — Open a new between-meetings resolution for voting. */
  async openOnlineResolution(payload: {
    condoId: string;
    title: string;
    description?: string;
    opensAt?: string;
    closesAt: string;
    quorumRequired?: number;
    passingThreshold?: number;
  }): Promise<any> {
    return this.request('/online-resolutions', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async castOnlineVote(resolutionId: string, payload: {
    voterId: string;
    choice: 'FOR' | 'AGAINST' | 'ABSTAIN';
    notes?: string;
  }): Promise<any> {
    return this.request(`/online-resolutions/${resolutionId}/vote`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async closeOnlineResolution(resolutionId: string): Promise<any> {
    return this.request(`/online-resolutions/${resolutionId}/close`, { method: 'POST' });
  }

  async cancelOnlineResolution(resolutionId: string): Promise<any> {
    return this.request(`/online-resolutions/${resolutionId}/cancel`, { method: 'POST' });
  }

  async tallyOnlineResolution(resolutionId: string): Promise<any> {
    return this.request(`/online-resolutions/${resolutionId}/tally`);
  }

  async listOnlineResolutionVotes(resolutionId: string): Promise<any[]> {
    return this.request(`/online-resolutions/${resolutionId}/votes`);
  }

  async listOnlineResolutionsForCondo(condoId: string): Promise<any[]> {
    return this.request(`/online-resolutions/condo/${condoId}`);
  }

  // ============ SPRINT 44 — STATUS CERT + AGM PACKET ============

  /** F39 — Server-side prefill for the cert wizard. */
  async prefillStatusCertificate(condoId: string, ownerId: string): Promise<any> {
    return this.request(`/status-certificates/prefill?condoId=${condoId}&ownerId=${ownerId}`);
  }

  /**
   * F41 — Email the cert PDF to a requestor. Pass the PDF the client
   * already rendered (jsPDF) so the server doesn't duplicate the
   * render. Gated on status_cert_email.
   */
  async emailStatusCertificate(certificateId: string, recipientEmail: string, pdfBlob?: Blob): Promise<any> {
    const form = new FormData();
    form.append('recipientEmail', recipientEmail);
    if (pdfBlob) form.append('pdf', pdfBlob, 'cert.pdf');
    const res = await apiFetch(`/status-certificates/${certificateId}/email`, {
      method: 'POST',
      body: form,
    });
    if (!res.ok) throw new Error(`Cert email failed: ${res.status}`);
    return res.json();
  }

  /** F42 — Generate an AGM packet. Returns the persisted AgmPacket row. */
  async generateAgmPacket(condoId: string, payload: {
    year: number;
    sections: string[];
    meetingDate?: string;
    location?: string;
    priorMinutes?: string;
    auditorName?: string;
    resolutionsText?: string;
  }): Promise<any> {
    return this.request(`/agm-packets/condo/${condoId}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async listAgmPackets(condoId?: string): Promise<any[]> {
    // Sprint 90 — defensive resolve + graceful-empty, matching the
    // getMaintenanceRequests pattern. The AGM list endpoint is tenant-scoped
    // and returns 403 when the agm_packet_bulk flag is off; short-circuit to
    // an empty list when no condo is resolvable so the page renders cleanly.
    const resolvedCondoId = condoId || this.resolveActiveCondoId();
    if (!resolvedCondoId) return [];
    return this.request(`/agm-packets/condo/${resolvedCondoId}`);
  }

  async downloadAgmPacket(packetId: string): Promise<Blob> {
    // Sprint 90 fix (QA F1): apiFetch already prepends the /api base, so the
    // leading '/api' here produced a 404 on '/api/api/agm-packets/...'.
    const res = await apiFetch(`/agm-packets/${packetId}/download`);
    if (!res.ok) throw new Error(`AGM packet download failed: ${res.status}`);
    return res.blob();
  }

  // ============ SPRINT 43 — MAINTENANCE + VIOLATIONS FINISH ============

  /** F24 — Maintenance photos. */
  async listMaintenancePhotos(requestId: string): Promise<any[]> {
    return this.request(`/maintenance-photos/request/${requestId}`);
  }
  async createMaintenancePhoto(payload: {
    requestId: string; condoId: string; s3Key: string;
    contentType?: string; sizeBytes?: number; caption?: string;
  }): Promise<any> {
    return this.request('/maintenance-photos', {
      method: 'POST', body: JSON.stringify(payload),
    });
  }
  async deleteMaintenancePhoto(id: string): Promise<void> {
    return this.request(`/maintenance-photos/${id}`, { method: 'DELETE' });
  }

  /** F27 — Recurring schedules: spawn-now + daily batch on demand. */
  async spawnFromSchedule(scheduleId: string): Promise<any> {
    return this.request(`/maintenance-schedules/${scheduleId}/spawn-now`, { method: 'POST' });
  }
  async runRecurringMaintenanceBatch(): Promise<any> {
    return this.request('/maintenance-schedules/run-now', { method: 'POST' });
  }

  /** F29 — Vendor scorecards + ratings. */
  async getVendorScorecard(condoId: string, vendorId: string): Promise<any> {
    return this.request(`/vendor-scorecards/condo/${condoId}/vendor/${vendorId}`);
  }
  async listVendorScorecards(condoId: string): Promise<any[]> {
    return this.request(`/vendor-scorecards/condo/${condoId}`);
  }
  /** Sprint 43 — submit a scorecard rating. Distinct from the
   *  pre-existing rateVendor() which writes Vendor.rating (a flat
   *  per-vendor field). This one writes a VendorRating audit row
   *  that the scorecard service aggregates over time. */
  async submitVendorScorecard(payload: {
    vendorId: string; condoId: string; stars: number;
    onTime?: boolean; complaint?: boolean; notes?: string;
    workOrderId?: string; maintenanceRequestId?: string;
  }): Promise<any> {
    return this.request('/vendor-scorecards/rate', {
      method: 'POST', body: JSON.stringify(payload),
    });
  }
  async listVendorRatings(condoId: string, vendorId: string): Promise<any[]> {
    return this.request(`/vendor-scorecards/ratings/condo/${condoId}/vendor/${vendorId}`);
  }

  /** F33 — Download a Violation Notice PDF. Returns Blob; gated on
   *  violations_pdf_export with 403 fall-back. */
  async fetchViolationPdf(violationId: string): Promise<Blob> {
    const res = await apiFetch(`/condo/violations/${violationId}/pdf`);
    if (!res.ok) throw new Error(`Violation PDF render failed: ${res.status}`);
    return res.blob();
  }

  // ============ SPRINT 42 — REPORTS FINISH ============

  /** Server-side Quarterly Report. */
  async getQuarterlyReportServer(condoId: string, quarter: number, year: number): Promise<any> {
    return this.request(`/reports/condo/${condoId}/quarterly?quarter=${quarter}&year=${year}`);
  }

  /** Server-side Year-to-Date Report. */
  async getYtdReportServer(condoId: string): Promise<any> {
    return this.request(`/reports/condo/${condoId}/ytd`);
  }

  /** Server-side Cash Flow Forecast. Gated on reports_cash_flow_forecast. */
  async getCashFlowForecast(condoId: string, monthsAhead = 12): Promise<any> {
    return this.request(`/reports/condo/${condoId}/cash-flow-forecast?months=${monthsAhead}`);
  }

  /** Reserve Fund 20-year forecast. Gated on reports_reserve_forecast. */
  async getReserveForecast(condoId: string): Promise<any[]> {
    return this.request(`/reports/condo/${condoId}/reserve-forecast`);
  }

  /** Reserve project CRUD (board / admin only). */
  async listReserveProjects(condoId: string): Promise<any[]> {
    return this.request(`/reserve-projects/condo/${condoId}`);
  }
  async createReserveProject(payload: any): Promise<any> {
    return this.request('/reserve-projects', { method: 'POST', body: JSON.stringify(payload) });
  }
  async updateReserveProject(id: string, payload: any): Promise<any> {
    return this.request(`/reserve-projects/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
  }
  async deleteReserveProject(id: string): Promise<void> {
    return this.request(`/reserve-projects/${id}`, { method: 'DELETE' });
  }

  /**
   * Render any report as a server-generated PDF. Returns a Blob so
   * the caller can trigger a download. Gated on reports_server_pdf —
   * a 403 means the condo hasn't licensed this feature; fall back to
   * the existing per-report frontend jsPDF flow.
   */
  async fetchReportPdf(condoId: string, type: 'pnl' | 'quarterly' | 'ytd' | 'ar-aging' | 'reserve-forecast',
                       params: Record<string, string | number> = {}): Promise<Blob> {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => qs.set(k, String(v)));
    // apiFetch already prepends the /api base (same fix as downloadAgmPacket,
    // Sprint 90 QA F1) — a leading '/api' here would hit '/api/api/reports/...'
    // and 404. Pass the path without the '/api' prefix.
    const url = `/reports/condo/${condoId}/pdf/${type}` + (qs.toString() ? `?${qs}` : '');
    const res = await apiFetch(url);
    if (!res.ok) throw new Error(`PDF render failed: ${res.status}`);
    return res.blob();
  }
}
